import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Music, Wrench, Award, Smartphone, ChevronRight } from 'lucide-react';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import ReviewsCarousel from '../components/ReviewsCarousel';
import { siteContent } from '../data/siteContent';
import { useTranslation } from '../context/TranslationContext';
import { useSanityProducts } from '../hooks/useSanityProducts';

function HomePage() {
  var { t } = useTranslation();
  var { products: allProducts, loading } = useSanityProducts({});
  
  // Get featured products (first 4 featured or just first 4)
  var featuredProducts = [];
  if (!loading && allProducts.length > 0) {
    var featured = allProducts.filter(function(p) { return p.featured; });
    featuredProducts = featured.length >= 4 ? featured.slice(0, 4) : allProducts.slice(0, 4);
  }
  
  var features = [
    { icon: Music, titleKey: 'professionalQuality', descKey: 'professionalQualityDesc' },
    { icon: Wrench, titleKey: 'innovativeDesign', descKey: 'innovativeDesignDesc' },
    { icon: Award, titleKey: 'expertKnowledge', descKey: 'expertKnowledgeDesc' },
    { icon: Smartphone, titleKey: 'toneRegistryAppTitle', descKey: 'toneRegistryAppDesc' }
  ];

  return (
    <Layout>
      <SEO 
        title="Innovative Gear for Wind Players"
        description="Shop NoteTurner, MuteClip, BelloFlex mutes & Real Books for ForScore. Custom-designed accessories by pro trumpet player Isaías Jiménez. Free shipping over $50."
        url="/"
      />
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={siteContent.brand.heroImage} alt="Isaías Jiménez" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="max-w-2xl">
            <p className="text-sm tracking-[0.3em] uppercase text-[#d4af37] mb-6 font-medium">{t('tagline')}</p>
            <h1 className="text-6xl md:text-8xl font-bold tracking-tight leading-[0.9] text-white mb-8">
              Isaías<br /><span className="text-[#d4af37]">Jiménez</span>
            </h1>
            <p className="text-xl text-neutral-300 leading-relaxed mb-10 max-w-lg">{t('heroDescription')}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-button" className="inline-flex items-center gap-2 bg-[#d4af37] text-black hover:bg-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105">
                {t('exploreShop')} <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/toneregistry" data-testid="hero-app-button" className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 px-8 py-4 rounded-full font-semibold backdrop-blur-sm transition-all">
                <Smartphone className="w-5 h-5" /> {t('toneRegistryApp')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Spotify Album Section */}
      <section className="py-12 bg-black border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-2">{t('listenNow')}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Art In Trumpet</h2>
              <p className="text-neutral-400 mb-4 max-w-lg">
                {t('albumDescription')}
              </p>
            </div>
            <div className="w-full md:w-auto md:flex-1 max-w-md">
              {/*
                Spotify's <iframe> embed occasionally crashes inside its own
                Next.js bundle (RangeError: "Incorrect locale information
                provided"). Our global error shim in src/index.js swallows
                cross-origin "Script error." and spotifycdn errors so the
                React dev overlay never pops because of it — the player just
                shows Spotify's own "Application error" placeholder for the
                affected user, while the rest of the site stays usable.
                loading="lazy" defers the iframe until it scrolls into view.
              */}
              <iframe
                title="Art In Trumpet - Spotify Album"
                src="https://open.spotify.com/embed/album/0FNNxh9GLN9B8ZegTYiCLt?utm_source=generator&theme=0"
                width="100%"
                height="152"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                data-testid="art-in-trumpet-spotify-embed-home"
                style={{ borderRadius: '12px' }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map(function(feature, index) {
              var Icon = feature.icon;
              return (
                <div key={index} className="text-center p-8 rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-[#d4af37]/30 transition-all">
                  <div className="w-14 h-14 mx-auto mb-6 rounded-2xl bg-[#d4af37]/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-[#d4af37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{t(feature.titleKey)}</h3>
                  <p className="text-neutral-500 text-sm">{t(feature.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="featured-products-section" className="py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-3">{t('shop')}</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white">{t('featuredProducts')}</h2>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-[#d4af37] hover:text-white transition-colors">
              {t('viewAllProducts')} <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map(function(product, index) {
              return <ProductCard key={product.slug || product._id} product={product} index={index} />;
            })}
          </div>
          <Link to="/shop" className="md:hidden mt-8 inline-flex items-center gap-2 text-[#d4af37]">
            {t('viewAllProducts')} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Customer Reviews Carousel */}
      <ReviewsCarousel />

      <section className="py-24 bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-4">{t('toneRegistryApp')}</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">{t('registerYourInstrument')}</h2>
              <p className="text-xl text-neutral-400 mb-8">{t('protectDesc')}</p>
              <div className="flex flex-wrap gap-4">
                <a href="https://apps.apple.com/us/app/toneregistry/id6756538205" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-white text-black hover:bg-neutral-200 px-6 py-4 rounded-xl font-semibold transition-all">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  {t('downloadOnAppStore')}
                </a>
                <span className="inline-flex items-center gap-2 text-neutral-500 px-4">{t('comingSoonAndroid')}</span>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/5 rounded-[3rem] blur-2xl" />
                <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl">
                  <img src="https://customer-assets.emergentagent.com/job_trumpet-innovations/artifacts/5i8r6bbt_Simulator%20Screenshot%20-%20iPhone%2017%20-%202025-12-25%20at%2001.04.46.png" alt="ToneRegistry App" className="w-72 h-auto object-contain rounded-[2.5rem]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default HomePage;
